import axios from 'axios';
import { API_BASE_URL } from './config/api';
import { 
  errorHandler, 
  handleApiError, 
  showUserFriendlyError,
  getRecoveryStrategy
} from './errorHandling';

// Enhanced retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 30000, // 30 seconds
  backoffMultiplier: 2
};

// Enhanced retry function using error handler
const executeWithRetry = async (operation, serviceName, options = {}) => {
  return errorHandler.executeWithRetry(operation, serviceName, {
    ...RETRY_CONFIG,
    ...options
  });
};

// Execute API operations with error handling
const executeWithFallback = async (primaryOperation, fallbackOperation, serviceName, options = {}) => {
  return errorHandler.executeWithFallback(primaryOperation, fallbackOperation, serviceName, {
    ...RETRY_CONFIG,
    ...options
  });
};

// Helper function to get auth token
const getAuthToken = () => {
  try {
    const nameEQ = "auth_token=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

// Helper function to create auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// API service for conversation management with enhanced error handling
export const conversationAPI = {
  // Get all conversations for the current user
  async getConversations(limit = 50, lastEvaluatedKey = null) {
    const fallbackOperation = () => {
      // No localStorage fallback - return empty state
      return { conversations: [], hasMore: false, lastEvaluatedKey: null };
    };

    return executeWithFallback(
      async () => {
        const params = new URLSearchParams({ limit: limit.toString() });
        if (lastEvaluatedKey) {
          params.append('lastEvaluatedKey', encodeURIComponent(JSON.stringify(lastEvaluatedKey)));
        }

        const response = await axios.get(`${API_BASE_URL}/conversations?${params}`, {
          headers: getAuthHeaders()
        });

        const result = {
          conversations: response.data.conversations || [],
          hasMore: response.data.hasMore || false,
          lastEvaluatedKey: response.data.lastEvaluatedKey || null
        };

        // No longer saving to localStorage fallback

        return result;
      },
      fallbackOperation,
      'conversationAPI.getConversations'
    );
  },

  // Create a new conversation
  async createConversation(title = 'New Chat') {
    return executeWithRetry(async () => {
      const response = await axios.post(`${API_BASE_URL}/conversations`, 
        { title },
        { headers: getAuthHeaders() }
      );

      return response.data.conversation;
    }, 'conversationAPI.createConversation');
  },

  // Update a conversation (e.g., title)
  async updateConversation(conversationId, updates) {
    return executeWithRetry(async () => {
      const response = await axios.put(`${API_BASE_URL}/conversations/${conversationId}`, 
        updates,
        { headers: getAuthHeaders() }
      );

      return response.data.conversation;
    }, 'conversationAPI.updateConversation');
  },

  // Delete a conversation
  async deleteConversation(conversationId) {
    return executeWithRetry(async () => {
      const response = await axios.delete(`${API_BASE_URL}/conversations/${conversationId}`, {
        headers: getAuthHeaders()
      });

      return response.data;
    }, 'conversationAPI.deleteConversation');
  }
};

// API service for message management with enhanced error handling
export const messageAPI = {
  // Get messages for a conversation with pagination
  async getConversationMessages(conversationId, limit = 50, beforeTimestamp = null) {
    const fallbackOperation = () => {
      // No localStorage fallback - return empty state
      return { messages: [], hasMore: false, oldestTimestamp: null };
    };

    return executeWithFallback(
      async () => {
        const params = new URLSearchParams({ limit: limit.toString() });
        if (beforeTimestamp) {
          params.append('before', beforeTimestamp.toString());
        }

        const response = await axios.get(`${API_BASE_URL}/conversations/${conversationId}/messages?${params}`, {
          headers: getAuthHeaders()
        });

        return {
          messages: response.data.messages || [],
          hasMore: response.data.hasMore || false,
          oldestTimestamp: response.data.oldestTimestamp || null
        };
      },
      fallbackOperation,
      'messageAPI.getConversationMessages'
    );
  },

  // Add a message to a conversation
  async addMessage(conversationId, role, content, model = '') {
    return executeWithRetry(async () => {
      const response = await axios.post(`${API_BASE_URL}/conversations/${conversationId}/messages`,
        { role, content, model },
        { headers: getAuthHeaders() }
      );

      return response.data.message;
    }, 'messageAPI.addMessage');
  }
};



// Error handling utilities for API operations
export const apiErrorUtils = {
  // Handle authentication errors
  handleAuthError: (error, onReauth) => {
    const classified = handleApiError(error, 'Authentication');
    if (classified.requiresReauth && onReauth) {
      console.log('Authentication required, triggering re-auth');
      onReauth();
    }
    return classified;
  },

  // Handle network errors with fallback
  handleNetworkError: (error, fallbackData = null) => {
    const classified = handleApiError(error, 'Network');
    if (classified.fallbackToLocal && fallbackData) {
      console.log('Network error, using fallback data');
      return {
        ...fallbackData,
        isFromFallback: true,
        fallbackReason: classified.userMessage
      };
    }
    throw error;
  },

  // Get user-friendly error message
  getUserFriendlyMessage: (error) => {
    return showUserFriendlyError(error);
  },

  // Check if error requires immediate user attention
  requiresUserAttention: (error) => {
    const classified = handleApiError(error);
    return classified.severity === 'CRITICAL' || classified.requiresReauth;
  },

  // Get recovery strategy for error
  getRecoveryStrategy: (error) => {
    return getRecoveryStrategy(error);
  }
};



